"use client";

import { cn } from "@aio/platform-ui";
import Link from "next/link";
import { usePathname } from "next/navigation";
import * as React from "react";

export interface SettingsSection {
  href: string;
  label: string;
}

export interface SettingsPageProps {
  title: string;
  description?: string;
  sections: readonly SettingsSection[];
  children: React.ReactNode;
}

export function SettingsPage({ title, description, sections, children }: SettingsPageProps) {
  const pathname = usePathname();
  return (
    <div className="space-y-6">
      <header>
        <h1 className="text-2xl font-semibold tracking-tight">{title}</h1>
        {description ? <p className="mt-1 text-sm text-fg-muted">{description}</p> : null}
      </header>
      <div className="grid gap-8 md:grid-cols-[14rem,1fr]">
        <nav className="flex flex-col gap-1 text-sm" aria-label="Settings sections">
          {sections.map((s) => {
            const active = pathname === s.href;
            return (
              <Link
                key={s.href}
                href={s.href as never}
                className={cn(
                  "rounded-md px-2 py-1.5",
                  active ? "bg-accent/10 text-accent-strong" : "text-fg hover:bg-muted",
                )}
              >
                {s.label}
              </Link>
            );
          })}
        </nav>
        <div className="space-y-6">{children}</div>
      </div>
    </div>
  );
}
